import { useCallback, useEffect, useRef, useState } from "react";
import ContentText from "./contentText.jsx";
import SectionContent from "./sectionContent.jsx";

/** Touch swipe hook — returns ref to attach + swipe handlers fire onPrev/onNext */
function useSwipe(onPrev, onNext) {
  const ref = useRef(null);
  const startX = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTouchStart = (e) => {
      startX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
      const delta = e.changedTouches[0].clientX - startX.current;
      if (delta > 50) onPrev();
      else if (delta < -50) onNext();
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onPrev, onNext]);

  return ref;
}

export function DetailsCarousel({ details }) {
  const [idx, setIdx] = useState(0);
  const total = details.length;
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const swipeRef = useSwipe(prev, next);

  return (
    <section
      ref={swipeRef}
      className="pt-10 pb-16 sm:py-20 bg-gray-50 flex flex-col items-center justify-center relative"
      data-testid="details-carousel"
      style={{ minHeight: "calc(100vh - 4rem)" }}
    >
      <SectionContent className="flex flex-col items-center flex-1 w-full">
        <h3 className="text-xl sm:text-2xl font-semibold text-center mb-2 sm:mb-4 min-h-[2rem]">
          {details[idx]?.title}
        </h3>
        {details[idx]?.description && (
          <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-base sm:text-lg text-center max-w-2xl">
            {details[idx].description}
          </p>
        )}

        {details[idx]?.image && (
          <div className="flex-1 flex items-center justify-center w-full overflow-hidden">
            <img
              key={idx}
              src={details[idx].image}
              alt={details[idx].title}
              className="max-w-full object-contain rounded-lg animate-fadeIn"
              style={{ maxHeight: "calc(100vh - 22rem)" }}
            />
          </div>
        )}
      </SectionContent>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-primary text-white shadow-lg rounded-full w-14 h-14 flex items-center justify-center z-10 hover:bg-primary800 transition-colors"
            aria-label="Föregående detalj"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-7 h-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <title>Föregående</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-primary text-white shadow-lg rounded-full w-14 h-14 flex items-center justify-center z-10 hover:bg-primary800 transition-colors"
            aria-label="Nästa detalj"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-7 h-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <title>Nästa</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <div className="flex justify-center gap-2 mt-6">
            {details.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${i === idx ? "bg-primary" : "bg-gray-300"}`}
                aria-label={`Gå till detalj ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function ExtrasCarousel({ extras }) {
  return (
    <section
      className="py-20 bg-white flex flex-col justify-center"
      data-testid="extras-section"
      style={{ minHeight: "calc(100vh - 4rem)" }}
    >
      <SectionContent>
        <h2 className="text-4xl font-bold text-center mb-12">
          <ContentText contentKey="machine.extras_heading" fallback="Möjliga tillval" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {extras.map((extra, i) => (
            <div key={i}>
              {extra.image && (
                <img
                  src={extra.image}
                  alt={extra.title}
                  className="w-full object-cover rounded-lg mb-4"
                  style={{ maxHeight: "28rem" }}
                />
              )}
              <h3 className="text-2xl font-semibold text-center mb-2">{extra.title}</h3>
              {extra.description && (
                <p className="text-gray-600 leading-relaxed text-lg text-center">
                  {extra.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </SectionContent>
    </section>
  );
}

export function VideoSection({ videos }) {
  return (
    <section data-testid="video-section">
      {/* First video — full width */}
      <div
        className="bg-black flex items-center justify-center"
        style={{ minHeight: "calc(100vh - 4rem)" }}
      >
        {/* biome-ignore lint/a11y/useMediaCaption: user-uploaded video */}
        <video
          src={videos[0]}
          controls
          className="w-full block"
          style={{ maxHeight: "calc(100vh - 4rem)" }}
        />
      </div>

      {/* Remaining videos in grid */}
      {videos.length > 1 && (
        <div className="py-12 bg-gray-50">
          <SectionContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.slice(1).map((url, i) => (
                <div key={i}>
                  {/* biome-ignore lint/a11y/useMediaCaption: user-uploaded video */}
                  <video src={url} controls className="w-full rounded-lg" />
                </div>
              ))}
            </div>
          </SectionContent>
        </div>
      )}
    </section>
  );
}
